import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, CheckCircle2, XCircle, Camera, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Card, EmptyState, Loading, Page, PageHeader } from "@/components/kit";
import { AuthImage, PhotoViewer } from "@/components/conference/PhotoCapture";

const toIso = (d) => d.toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
const shift = (iso, days) => { const d = new Date(`${iso}T12:00`); d.setDate(d.getDate() + days); return toIso(d); };
const monday = (iso) => { const d = new Date(`${iso}T12:00`); return shift(iso, -((d.getDay() + 6) % 7)); };
const br = (iso) => new Date(`${iso}T12:00`).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
const tone = (r) => (r >= 90 ? "var(--success)" : r >= 70 ? "var(--warning, #C98A1B)" : "var(--danger)");

function Rate({ value, testid }) {
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "var(--surface-alt)" }}><div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${value}%`, background: tone(value) }} /></div>
      <span className="label tnum w-12 text-right" style={{ color: tone(value) }} data-testid={testid}>{Math.round(value)}%</span>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return <Card className="flex flex-col gap-1"><div className="caption">{label}</div><div className="h2 tnum" data-testid={testid}>{value}</div></Card>;
}

function UserRow({ u }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-3" data-testid={`report-user-${u.id}`}>
      <button type="button" className="w-full flex flex-wrap items-center gap-3 text-left" onClick={() => setOpen(!open)} data-testid={`report-user-toggle-${u.id}`}>
        <div className="flex-1 min-w-[140px]"><div className="label">{u.name}</div><div className="caption tnum">{u.done} de {u.expected} feitas{u.missed.length ? ` · ${u.missed.length} pendente(s)` : ""}</div></div>
        <Rate value={u.rate} testid={`report-user-rate-${u.id}`} />
      </button>
      {open && u.missed.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1 pl-1">{u.missed.map((m) => <li key={`${m.task_id}-${m.date}`} className="caption flex items-center gap-2"><XCircle size={13} style={{ color: "var(--danger)" }} /> {br(m.date)} · {m.title}</li>)}</ul>
      )}
    </div>
  );
}

function Photos({ photos, onView }) {
  if (!photos.length) return <p className="caption">Nenhuma foto enviada nesta semana.</p>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="report-photos">
      {photos.map((p) => (
        <div key={`${p.task_id}-${p.user_id}-${p.date}`} className="flex flex-col gap-1">
          <AuthImage path={p.photo_path} className="aspect-square w-full rounded-xl" onClick={() => onView(p.photo_path)} testid={`report-photo-${p.task_id}-${p.user_id}-${p.date}`} />
          <div className="caption truncate">{br(p.date)} · {p.user_name}</div>
          <div className="caption truncate" style={{ color: "var(--text)" }}>{p.title}</div>
        </div>
      ))}
    </div>
  );
}

export default function TasksReport() {
  const thisWeek = monday(toIso(new Date()));
  const [start, setStart] = useState(thisWeek);
  const [view, setView] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["tasks-report", start], queryFn: () => api(`/tasks/report?start=${start}`) });
  const s = data?.summary;
  return (
    <Page>
      <PageHeader title="Relatório semanal de tarefas" subtitle="Conclusão da equipe e fotos de comprovação · enviado por e-mail toda segunda" back="/tasks" />
      <div className="flex items-center gap-2 mb-5">
        <button className="icon-btn" onClick={() => setStart(shift(start, -7))} data-testid="report-prev-week"><ChevronLeft size={16} /></button>
        <div className="label tnum min-w-[150px] text-center" data-testid="report-week-label">{br(start)} a {br(shift(start, 6))}</div>
        <button className="icon-btn" disabled={start >= thisWeek} onClick={() => setStart(shift(start, 7))} data-testid="report-next-week"><ChevronRight size={16} /></button>
        {start !== thisWeek && <button className="chip" onClick={() => setStart(thisWeek)} data-testid="report-this-week">Esta semana</button>}
      </div>
      {isLoading ? <Loading /> : !s?.expected ? <EmptyState title="Sem tarefas nesta semana" text="Nenhuma tarefa estava prevista para a equipe neste período." testid="report-empty" /> : (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Conclusão" value={`${Math.round(s.rate)}%`} testid="report-kpi-rate" />
            <Kpi label="Tarefas feitas" value={`${s.done}/${s.expected}`} testid="report-kpi-done" />
            <Kpi label="Não feitas" value={s.missed} testid="report-kpi-missed" />
            <Kpi label="Fotos" value={s.photos} testid="report-kpi-photos" />
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="divide" data-testid="report-by-user"><div className="label flex items-center gap-2 pb-2"><Users size={15} /> Por colaborador</div>{data.by_user.map((u) => <UserRow key={u.id} u={u} />)}</Card>
            <Card className="divide" data-testid="report-by-task"><div className="label flex items-center gap-2 pb-2"><CheckCircle2 size={15} /> Por tarefa</div>
              {data.by_task.map((t) => <div key={t.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`report-task-${t.id}`}><div className="flex-1 min-w-[140px]"><div className="label">{t.title}{t.require_photo && <Camera size={12} className="inline ml-1.5" style={{ color: "var(--brand)" }} />}</div><div className="caption tnum">{t.done} de {t.expected}</div></div><Rate value={t.rate} testid={`report-task-rate-${t.id}`} /></div>)}
            </Card>
          </div>
          <Card><div className="label flex items-center gap-2 pb-3"><Camera size={15} /> Fotos da semana</div><Photos photos={data.photos} onView={setView} /></Card>
        </div>
      )}
      <PhotoViewer path={view} onClose={() => setView(null)} />
    </Page>
  );
}
